import type { BoxCollider, FloorRegion } from './collision';
import { PLAYER_RADIUS } from './controller';
import { TUNNEL_FLOOR } from './layout';

/**
 * Wave D-1 (PLAN-CITY2 §4): the Rat King's basement lair, hosting the real
 * `RatKingBoss` (ai/ratking.ts, built by Wave M/C08 but never spawned until
 * now — see that file's own header comment). Reached from a visual breach
 * panel at the utility tunnel's own dead end (`TUNNEL_FLOOR`'s maxZ=40,
 * world/layout.ts) via a walk-through teleport trigger, matching every other
 * interior room's isolated-origin pattern (world/interior.ts's header
 * comment on why a teleport door beats carving a real gap into shipped
 * geometry) — `blk_tunnel_breach`'s own NOTES.md confirms the panel itself
 * carries zero collision, "the visual read only," so the tunnel's existing
 * reviewed dead-end wall (world/layout.ts's `allWalls()`, topY:40) stays
 * fully intact; nothing here touches it.
 *
 * `LAIR_ORIGIN` (x=480) sits far past every existing isolated room's own
 * self-heal threshold (INTERIOR_ORIGIN=140, LAUNDROMAT_ORIGIN=250,
 * BASEMENT_ORIGIN=360 — each >100m separated from this one and from each
 * other), so it can never be reached except by the explicit teleport, per
 * the same reasoning every prior interior's own header comment gives.
 */
export const LAIR_ORIGIN = { x: 480, z: 0 };

const HALF_WIDTH = 9; // X — 18.0m floor, matches the delivered blk_basement_lair.glb exactly
const HALF_DEPTH = 7; // Z — 14.0m floor
export const LAIR_ROOM = {
  minX: LAIR_ORIGIN.x - HALF_WIDTH,
  maxX: LAIR_ORIGIN.x + HALF_WIDTH,
  minZ: LAIR_ORIGIN.z - HALF_DEPTH,
  maxZ: LAIR_ORIGIN.z + HALF_DEPTH,
  height: 6.0,
};

/**
 * The C07 delivery's own doorway: 2.2m×2.2m, centered on the EAST 14m wall
 * (x=+9 local — the wall opposite the 3 rat holes below, per NOTES.md's own
 * "one east short-wall doorway"). `DOOR_HALF_WIDTH` mirrors
 * world/interior.ts's own constant of the same name/purpose exactly.
 */
const DOOR_HALF_WIDTH = 1.1;
const DOOR_HALF_HEIGHT_Y = 2.2;

/** Just inside the east doorway, facing west into the room — same "just inside the near wall" idiom as INTERIOR_SPAWN (world/interior.ts). */
export const LAIR_SPAWN = { x: LAIR_ROOM.maxX - 1.5, z: LAIR_ORIGIN.z };

/**
 * Rat-hole swarm-call spawn points (ai/ratking.ts's `RatKingTickResult.
 * swarmCallFired` — the manager, `world/denLair.ts`'s own `LairEncounter`,
 * spawns `SWARM_RAT_COUNT` rats near these on that tick). One per 18m wall
 * (north/south) + one on the 14m wall OPPOSITE the doorway (west) — matches
 * the delivered GLB's own `north_hole`/`south_hole`/`west_hole` node
 * positions exactly (their world-space centers, per the independent GLB
 * inspection this session ran before writing any code). Inset 1m from the
 * wall face so a spawned rat doesn't materialize partway inside the wall
 * collider.
 */
export const LAIR_RAT_HOLES: Array<{ x: number; z: number }> = [
  { x: LAIR_ORIGIN.x, z: LAIR_ROOM.maxZ - 1 }, // north
  { x: LAIR_ORIGIN.x, z: LAIR_ROOM.minZ + 1 }, // south
  { x: LAIR_ROOM.minX + 1, z: LAIR_ORIGIN.z }, // west (opposite the east doorway)
];

/**
 * 6 pillar instances (footprint 0.8×0.8, per the brief's "4-6 instances")
 * — a 3×2 grid, x∈{-6,-1,5} (local) × z∈{-4,4} (local), hand-checked clear
 * of every wall/doorway/rat-hole with real margin (nearest: the x=5 column
 * sits 3.6m off the east doorway wall; every z=±4 row sits 2.6m off the
 * north/south walls) and clear of `RATKING_HOME` (room center) so the fight
 * has real dodge room around them. `pillarIndex` keys `LairEncounter`'s own
 * intact/cracked state array — the RED CHARGE hook (see that class) marks
 * whichever pillar the boss's charge stops against as cracked, keyed by this
 * same index.
 */
export interface LairPillarSpec {
  x: number;
  z: number;
}
const PILLAR_LOCAL: Array<[number, number]> = [
  [-6, -4],
  [-6, 4],
  [-1, -4],
  [-1, 4],
  [5, -4],
  [5, 4],
];
export const LAIR_PILLARS: LairPillarSpec[] = PILLAR_LOCAL.map(([lx, lz]) => ({ x: LAIR_ORIGIN.x + lx, z: LAIR_ORIGIN.z + lz }));
const PILLAR_HALF = 0.4; // 0.8m footprint

export const RATKING_HOME = { x: LAIR_ORIGIN.x, z: LAIR_ORIGIN.z };

/**
 * Camera: the 6.0m lair ceiling comfortably clears the outdoor NORMAL/FAR
 * zoom presets at default pitch (core/camera.ts's `CAMERA_ZOOM_DISTANCES` —
 * FAR=4.92m puts the camera ~3.57m up at pitch 0.55rad, well under 6.0m) and
 * the 18×14m floor comfortably clears every preset's own horizontal pull-back
 * — no interior maxDistance clamp needed (the brief's own "your call," taken
 * here: unclamped). `lair_ceiling` is hidden in code instead (see
 * `Block.attachDenLair()`), matching the delivered GLB's own naming
 * convention for "the engine has no overhead collision yet."
 */
export const LAIR_CAMERA_MAX_DISTANCE: number | undefined = undefined;

/** Four solid walls with the east doorway gap, mirroring `interiorWalls()`'s exact segment idiom (world/interior.ts). */
export function lairWalls(): BoxCollider[] {
  const t = 0.4;
  const r = LAIR_ROOM;
  const doorCenterZ = LAIR_ORIGIN.z;
  return [
    { minX: r.minX, maxX: r.maxX, minZ: r.minZ, maxZ: r.minZ + t, topY: r.height, bottomY: 0 }, // south
    { minX: r.minX, maxX: r.maxX, minZ: r.maxZ - t, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // north
    { minX: r.minX, maxX: r.minX + t, minZ: r.minZ, maxZ: r.maxZ, topY: r.height, bottomY: 0 }, // west
    // east — split around the doorway gap
    { minX: r.maxX - t, maxX: r.maxX, minZ: r.minZ, maxZ: doorCenterZ - DOOR_HALF_WIDTH, topY: r.height, bottomY: 0 },
    { minX: r.maxX - t, maxX: r.maxX, minZ: doorCenterZ + DOOR_HALF_WIDTH, maxZ: r.maxZ, topY: r.height, bottomY: 0 },
  ];
}

export const LAIR_FLOOR: FloorRegion = {
  kind: 'flat',
  minX: LAIR_ROOM.minX,
  maxX: LAIR_ROOM.maxX,
  minZ: LAIR_ROOM.minZ,
  maxZ: LAIR_ROOM.maxZ,
  y: 0,
  priority: 1,
};

/** Pillar colliders — each a real wall (per `PILLAR_HALF`'s 0.8m footprint), well below `VAULT_MAX_HEIGHT`... no: pillars are 6.0m tall, genuinely solid, never vaultable (topY far exceeds maxVaultHeight, so `findVaultLedge` rejects them automatically). Indexed 1:1 with `LAIR_PILLARS`. */
export function lairPillarColliders(): BoxCollider[] {
  return LAIR_PILLARS.map((p) => ({
    minX: p.x - PILLAR_HALF,
    maxX: p.x + PILLAR_HALF,
    minZ: p.z - PILLAR_HALF,
    maxZ: p.z + PILLAR_HALF,
    topY: LAIR_ROOM.height,
    bottomY: 0,
  }));
}

// --- Tunnel breach (visual dressing at the tunnel's own z=40 dead end) ---
/** Matches `blk_tunnel_breach`'s own authored size (6.0m wide × 2.6m tall) and NOTES.md's `placementY: -3`. Centered on the tunnel's own X center ((14+20)/2=17), mounted on the dead-end back wall (z=40). */
export const TUNNEL_BREACH_CENTER = { x: 17, z: TUNNEL_FLOOR.maxZ };
export const TUNNEL_BREACH_PLACEMENT_Y = -3;
const BREACH_HALF_WIDTH = 3.0;

/**
 * Walk-through trigger, T3's door law exactly (a bounded 3D box + input-
 * intent, world/interior.ts's `isAtBodegaDoorFace` is the reference this
 * mirrors): the tunnel's own back wall (world/layout.ts `allWalls()`,
 * topY:40 — deliberately untouched, see this file's header comment) still
 * physically blocks a walk past z=40, so this fires just BEFORE contact,
 * approaching from inside the tunnel (z<40, y≈-3 tunnel-floor level).
 */
const APPROACH_DEPTH = 0.3;
const TRIGGER_MAX_Z = TUNNEL_BREACH_CENTER.z;
const TRIGGER_MIN_Z = TRIGGER_MAX_Z - PLAYER_RADIUS - APPROACH_DEPTH;
export function isAtTunnelBreachFace(x: number, y: number, z: number): boolean {
  return (
    z >= TRIGGER_MIN_Z &&
    z <= TRIGGER_MAX_Z &&
    y < -1 && // deep inside the tunnel (floor is y=-3), never a street-level false-positive
    Math.abs(x - TUNNEL_BREACH_CENTER.x) <= BREACH_HALF_WIDTH + 0.2
  );
}

/** Walking back out through the lair's own east doorway — the walk-out exit, mirroring `isPastDoorwayThreshold` (world/interior.ts). Fires once the player's center clears the doorway's outer (east) face. */
const DOORWAY_BAND_MARGIN = PLAYER_RADIUS + 0.05;
export function isPastLairDoorwayThreshold(x: number, z: number): boolean {
  return (
    x > LAIR_ROOM.maxX &&
    z > LAIR_ORIGIN.z - DOOR_HALF_WIDTH - DOORWAY_BAND_MARGIN &&
    z < LAIR_ORIGIN.z + DOOR_HALF_WIDTH + DOORWAY_BAND_MARGIN
  );
}

/** Where exiting the lair lands the player — just south of the tunnel's own dead end, matching the tunnel-floor's real Y (no teleport-tolerance surprises). */
export const TUNNEL_BREACH_STANDPOINT = { x: TUNNEL_BREACH_CENTER.x, z: TUNNEL_BREACH_CENTER.z - 2.5 };

/**
 * K-2/L5: the tunnel breach becomes a real INTERACT-button prompt ("every
 * TELEPORT-gated den entrance has an INTERACT prompt like every other
 * enterable thing") instead of the old walk-through auto-teleport — main.ts
 * now gates `enterLair()` on `hud.consumeInteractPress()`, matching
 * bodega/laundromat/basement. Offset toward the EAST wall (x=19, not the
 * breach's own dead-center x=17) specifically to clear `HIDDEN_STASH_MARKER`
 * (15.5, 38.5, radius 1.8) with real margin — at (19, 39.4) the two centers
 * sit 3.64m apart, comfortably beyond the ≥0.2m-margin bar (radius 1.1 here
 * + 1.8 there = 2.9, leaving 0.74m clear) K-2's own findability package
 * calls for. `isAtTunnelBreachFace` (below) still gates the PROMPT's own
 * approach zone; this marker is only the interactable's trigger center.
 */
export const LAIR_ENTRANCE_MARKER = { x: 19, z: 39.4 };

// D-4: pillar footprint half-width re-exported for Block.ts's collision-vs-charge proximity check (LairEncounter's own pillar-hit detection).
export const LAIR_PILLAR_HALF = PILLAR_HALF;
export { DOOR_HALF_HEIGHT_Y as LAIR_DOOR_HALF_HEIGHT };
